/**
 * Single source of truth for every piece of copy on the site.
 *
 * Wording is taken verbatim from Manjeet's resume and LinkedIn profile.
 * Update this file — not the components — when the resume changes.
 */

export const profile = {
  name: "Manjeet Dhayal",
  headline:
    "Core Infrastructure Engineer @ Oracle | Building AI/RAG systems | Founder @ GetQi",
  role: "Core Infrastructure Engineer",
  company: "Oracle",
  location: "Bengaluru, India",
  email: "manjeetdhayal786@gmail.com",
  phone: "+917297006153",
};

export const links = {
  linkedin: "https://www.linkedin.com/in/manjeet-dhayal",
  github: "https://github.com/manjeetdhayal",
  leetcode: "https://leetcode.com/manjeetdhayal",
  youtube: "https://www.youtube.com/@thinkbeyondcoding",
  twitter: "https://twitter.com/coder_manjeet",
  instagram: "https://www.instagram.com/___manjeet_",
  email: "mailto:manjeetdhayal786@gmail.com",
};

/* --- About --------------------------------------------------------------- */

export const about = {
  /* Intro paragraph, segmented so key nouns can be emphasised in the markup
     without embedding HTML in the copy. Each entry is [text] or [text, true]. */
  introParts: [
    ["I'm a Software Engineer at "],
    ["Oracle", true],
    [
      ", where I build backend, infrastructure and AI/RAG systems that solve real-world problems at scale. I also founded ",
    ],
    ["GetQi", true],
    [
      ", a corporate wellness platform in India, because I believe great ideas deserve to go beyond just code.",
    ],
  ],
  quote:
    "I like building systems that are simple on the surface and interesting underneath.",
  story: [
    "Growing up, I dismantled toys and gadgets at sight, trying to understand how they worked. That curiosity — and the urge to make things better — has stayed with me.",
    "Over the years, I've worked on scalable systems, backend platforms, and AI-powered solutions, and I enjoy the challenge of turning complex problems into elegant, reliable systems.",
  ],
  focus: ["Backend Systems", "AI / RAG", "Distributed Systems", "Product Building"],
  closing: "Want to learn more about me? Let's connect!",
  facts: [
    { key: "Based in", val: "Bengaluru, India" },
    { key: "Currently", val: "Core Infrastructure Engineer, Oracle" },
    { key: "Also building", val: "GetQi" },
    { key: "Studied", val: "CSE, NIT Rourkela" },
  ],
  cards: [
    {
      icon: "building",
      label: "Experience",
      title: "Software Engineer",
      meta: "Oracle Cloud Infrastructure",
      body: "Building AI/RAG systems, backend and infrastructure solutions.",
    },
    {
      icon: "rocket",
      label: "Founder",
      title: "GetQi",
      meta: "Corporate Wellness Platform",
      body: "Building a healthier, happier workplace — in India.",
    },
  ],
};

/* --- Technical skills: resume ---------------------------------------------- */

export const skills = [
  {
    icon: "code",
    title: "Languages",
    items: ["Java", "JavaScript/TypeScript", "C/C++", "Dart", "SQL"],
  },
  {
    icon: "layers",
    title: "Frameworks",
    items: [
      "Spring Boot",
      "Dropwizard",
      "React",
      "Next.js",
      "Node.js/Express",
      "Flutter",
      "TailwindCSS",
    ],
  },
  {
    icon: "cloud",
    title: "Tools & Cloud",
    items: [
      "Git",
      "Docker",
      "OCI",
      "MySQL",
      "PostgreSQL",
      "MongoDB",
      "Firebase",
      "Postman",
    ],
  },
  {
    icon: "spark",
    title: "AI/LLM",
    items: [
      "LLM Integration",
      "Prompt Engineering",
      "RAG",
      "Tool Calling",
      "AI Coding Assistants",
      "MCP",
      "Agentic AI",
    ],
  },
];

/* --- Experience: grouped by organisation, LinkedIn-style ------------------- */

export const experience = [
  {
    id: "industry",
    label: "Industry",
    desc: "Professional engineering roles",
    orgs: [
      {
        org: "Oracle",
        mark: "O",
        tenure: "2 years 2 months",
        roles: [
          {
            title: "Core Infrastructure Engineer 1",
            duration: "May 2026 – Present",
            type: "Full-time",
            location: "Bengaluru",
            current: true,
            stack: ["Java", "Dropwizard", "OCI", "TypeScript", "React", "RAG"],
            bullets: [
              "Reduced API latency from 60s to sub-second (600ms - 1s), a ~98% improvement, by tuning JDBC connection pool configuration and introducing a denormalized table to eliminate a full table scan on a heavy DB query.",
            ],
          },
          {
            title: "Member of Technical Staff",
            duration: "Jul 2024 – May 2026",
            type: "Full-time",
            location: "Bengaluru",
            stack: ["Java", "Dropwizard", "OCI", "TypeScript", "React", "RAG"],
            bullets: [
              "Built and scaled high-performance backend services for Oracle Cloud Infrastructure using Java and Dropwizard, supporting enterprise-scale distributed systems.",
              "Designed and built an AI-powered on-call RCA system (Project Ace) at the OCI Platform Hackathon, leveraging Retrieval-Augmented Generation (RAG) over historical Slack conversations; integrated Slack Search APIs with an AI model to generate context-aware RCA insights and actionable resolution steps, achieving ~90%+ accuracy in identifying root causes.",
              "Extended Ace with automated remediation workflows - including auto-generated pull requests for common fixes (e.g., vulnerability patches) - reducing incident resolution cycles and improving developer productivity.",
              "Designed resilient distributed systems and optimized data access layers (SQL/NoSQL), improving performance and enabling scalable resource management for growing customer demand.",
              "Developed internal and customer-facing applications using React and TypeScript, working within complex internal frameworks and secure access patterns.",
              "Implemented dynamic workflow-driven systems to handle custom operational requests, enabling flexible, configuration-based processes without code changes.",
            ],
          },
          {
            title: "Server Technology Intern",
            duration: "May 2023 – Jul 2023",
            type: "Internship",
            location: "Bengaluru",
            stack: ["Oracle Functions", "Docker", "GraalVM"],
            bullets: [
              "Developed runtime environment image for Oracle GraalVM to provide users with native image support.",
              "Implemented automated integration of images into the FDK release pipeline, ensuring seamless updates with every major release of fn-fdk and GraalVM.",
              "Collaborated with the GraalVM team to address architecture-related challenges in generated images.",
            ],
          },
        ],
      },
      {
        org: "GetQi",
        mark: "G",
        tenure: "6 months",
        roles: [
          {
            title: "Founder & Software Engineer (Side Project)",
            duration: "Mar 2026 – Present",
            type: "Part-time",
            location: "Remote",
            current: true,
            stack: ["Java", "Spring Boot", "PostgreSQL", "Next.js", "TypeScript"],
            bullets: [
              "Building Getqi as a part-time SaaS venture alongside my role at Oracle.",
              "Founded and architected Getqi, a B2B2C health and wellness platform connecting enterprises, fitness facilities, personal trainers, and consumers through memberships, passes, and QR-based check-ins.",
              "Designed and developed a scalable backend using Java, Spring Boot, PostgreSQL, JWT authentication, and REST APIs, serving both web and mobile applications.",
              "Built responsive web and mobile applications using Next.js, TypeScript, and Flutter, implementing secure role-based access control, real-time workflows, and seamless user experiences.",
              "Implemented production-grade features including Razorpay payment integration, dual-wallet architecture, scheduled background jobs, QR-based access control, and secure transaction processing.",
            ],
          },
        ],
      },
      {
        org: "AulaCube Technologies Private Limited",
        mark: "A",
        tenure: "3 months",
        roles: [
          {
            title: "Front End Development Intern",
            duration: "Dec 2023 – Feb 2024",
            type: "Internship",
            location: "Remote",
            stack: ["React", "JavaScript"],
            bullets: [],
          },
        ],
      },
      {
        org: "Brick&Bolt",
        mark: "B",
        tenure: "3 months",
        roles: [
          {
            title: "React JS Intern",
            duration: "Jun 2022 – Aug 2022",
            type: "Internship",
            location: "Remote",
            stack: ["React"],
            note: "Worked on customer app frontend development to enhance the user experience.",
          },
        ],
      },
      {
        org: "MeMeraki",
        mark: "M",
        tenure: "5 months",
        roles: [
          {
            title: "React Tech Intern",
            duration: "Feb 2022 – Jun 2022",
            type: "Internship",
            location: "Remote",
            stack: ["React", "Shopify"],
            note: "Optimized website to enhance user experience based on user feedback. Rebuilt masterclass purchase section from scratch to give users a seamless buying experience.",
          },
        ],
      },
    ],
  },
  {
    id: "leadership",
    label: "Leadership",
    desc: "Organising events and leading teams",
    orgs: [
      {
        org: "Hackodisha 2.0",
        mark: "H",
        tenure: "5 months",
        roles: [
          {
            title: "Lead Organizer",
            duration: "Jun 2022 – Oct 2022",
            type: "Leadership",
            location: "Remote",
            note: "Lead Organizer and Technical Team Lead. Developed the Hackodisha 2.0 Website from scratch along with the team. It was an enriching experience with very curious, and talented teammates.",
          },
        ],
      },
      {
        org: "WEBWIZ.NITR",
        mark: "W",
        tenure: "1 year 7 months",
        roles: [
          {
            title: "President",
            duration: "Aug 2021 – Jun 2022",
            type: "Leadership",
            location: "NIT Rourkela",
          },
          {
            title: "Web Developer",
            duration: "Dec 2020 – Feb 2022",
            type: "Member",
            location: "NIT Rourkela",
          },
        ],
      },
      {
        org: "Codecademy",
        mark: "C",
        tenure: "1 year",
        roles: [
          {
            title: "Chapter Lead",
            duration: "Jun 2021 – May 2022",
            type: "Leadership",
            location: "Remote",
          },
        ],
      },
    ],
  },
  {
    id: "community",
    label: "Community",
    desc: "Mentoring and open source",
    orgs: [
      {
        org: "GirlScript Winter of Contributing",
        mark: "G",
        tenure: "4 months",
        roles: [
          {
            title: "Supervisor",
            duration: "Aug 2021 – Nov 2021",
            type: "Open Source",
            location: "Remote",
          },
        ],
      },
      {
        org: "Hackodisha",
        mark: "H",
        tenure: "10 months",
        roles: [
          {
            title: "Core Organiser",
            duration: "May 2021 – Feb 2022",
            type: "Community",
            location: "NIT Rourkela",
          },
        ],
      },
      {
        org: "Script Foundation",
        mark: "S",
        tenure: "5 months",
        roles: [
          {
            title: "Fellow",
            duration: "Aug 2021 – Dec 2021",
            type: "Fellowship",
            location: "Remote",
          },
        ],
      },
      {
        org: "NPSiHACKS",
        mark: "N",
        tenure: "1 month",
        roles: [
          {
            title: "Hackathon mentor",
            duration: "Jul 2021",
            type: "Mentorship",
            location: "Remote",
            note: "Mentored very enthusiastic hackers and young minds and really glad to help. It was my first time mentoring experience and gained a lot of skills form it.",
          },
        ],
      },
    ],
  },
];

/* --- Projects -------------------------------------------------------------- */

export const projects = [
  {
    id: "getqi",
    name: "GetQi",
    cover: "wellness",
    image: null,
    github: "",
    website: "",
    tags: ["Java", "Spring Boot", "PostgreSQL", "Next.js", "Flutter"],
    desc: "A B2B2C health and wellness platform connecting enterprises, fitness facilities, personal trainers, and consumers through memberships, passes, and QR-based check-ins. Production features include Razorpay payment integration, dual-wallet architecture, scheduled background jobs, QR-based access control, and secure transaction processing.",
  },
  {
    id: "ace",
    name: "Ace — AI On-Call RCA",
    cover: "ai",
    image: null,
    github: "",
    website: "",
    tags: ["RAG", "LLM", "Slack API", "Java"],
    desc: "An AI-powered on-call RCA system built at the OCI Platform Hackathon, leveraging Retrieval-Augmented Generation over historical Slack conversations to generate context-aware RCA insights and actionable resolution steps, achieving ~90%+ accuracy in identifying root causes.",
  },
  {
    id: "heartrate",
    name: "Heart Rate Monitoring and Virtual Support App",
    cover: "health",
    image: null,
    github: "",
    website: "",
    tags: ["Flutter", "Dart", "tflite", "Agora SDK", "Firebase"],
    desc: "A heart rate monitoring app using a stethoscope with custom amplifier; performs signal denoising and quality assessment, then classifies using a tflite model (85% accuracy) to detect abnormal heart rate. Routes users to a Doctor Consultation Dashboard with real-time video conferencing via Agora SDK.",
  },
  {
    id: "anmolcareer",
    name: "Anmol Career",
    image: "AnmolCareer",
    github: "",
    website: "https://www.anmolcareer.in",
    tags: ["React", "Node.js", "MongoDB"],
    desc: "Career coaching platform where users can login, browse and enrol in courses. Admin dashboard for managing courses, materials, users, and CRUD operations.",
  },
  {
    id: "shopit",
    name: "ShopIt",
    image: "ShopIt",
    github: "https://github.com/manjeetdhayal/shopit",
    website: "",
    tags: ["React", "Redux", "Express"],
    desc: "Full-featured e-commerce app — buy, review, rate, and comment on products. Admin panel handles orders, inventory, users, and review moderation.",
  },
  {
    id: "letmehandle",
    name: "LetMeHandle",
    image: "LetMeHandle",
    github: "https://github.com/manjeetdhayal/LetMeHandle",
    website: "",
    tags: ["React", "Firebase"],
    desc: "Team task management app. Assign tasks to members, track progress, and manage project work — built for real team-level collaboration.",
  },
];

/* --- Certifications -------------------------------------------------------- */

export const certifications = [
  {
    title: "Oracle Certified Professional: Java SE 21 Developer",
    issuer: "Oracle",
    date: "Aug 2026",
  },
];

/* --- Education ------------------------------------------------------------- */

export const education = [
  {
    institution: "National Institute of Technology Rourkela",
    degree: "Computer Science and Engineering, B.Tech",
    period: "Nov 2020 – Jun 2024",
    location: "Odisha, India",
    stats: [
      { val: "8.73/10", key: "CGPA" },
      { val: "2020–24", key: "Years" },
    ],
    desc: "Led WEBWIZ — the institute's web development club — as President, and organised Hackodisha, a national-level hackathon.",
  },
  {
    institution: "Pilani Public School, Pilani",
    degree: "Senior Secondary Education",
    period: "2016 – 2019",
    location: "Rajasthan, India",
    stats: [],
    desc: "",
  },
];

export const navigation = [
  { name: "About", href: "#about" },
  { name: "Skills", href: "#skills" },
  { name: "Experience", href: "#experience" },
  { name: "Projects", href: "#projects" },
  { name: "Education", href: "#education" },
];

/* --- Positioning --------------------------------------------------------- */
/* Curated summary used for the meta description, JSON-LD, the accessible
   hero blurb, and the generated resume.json / llms.txt. Keep it truthful and
   backed by the experience bullets above. */

export const positioning = {
  summary:
    "Backend, infrastructure and AI/RAG engineer at Oracle Cloud Infrastructure. I build Java and Dropwizard services and distributed systems at enterprise scale, ship LLM/RAG systems that hold up in production, and founded GetQi, a B2B2C corporate wellness platform.",
  openTo: [
    "Backend Engineer",
    "Platform / Infrastructure Engineer",
    "Distributed Systems Engineer",
    "AI / LLM Systems Engineer",
    "Founding Engineer",
  ],
  highlights: [
    "Cut a hot API path from 60s to sub-second (~98% faster) by tuning JDBC connection-pool configuration and replacing a full table scan with a denormalized table.",
    "Built Ace, an AI on-call RCA system using Retrieval-Augmented Generation over Slack history (~90%+ root-cause accuracy), then extended it to open auto-remediation pull requests.",
    "Ships production Java/Dropwizard backend services for Oracle Cloud Infrastructure supporting enterprise-scale distributed systems.",
    "Founder of GetQi: architected a B2B2C wellness platform end to end — Java/Spring Boot backend, Next.js and Flutter clients, Razorpay payments, dual-wallet, QR-based access control.",
  ],
};

/* Skill keywords grouped by how strongly each is backed by real work, so
   automated resume matching does not over- or under-weight a claim. */

export const skillTiers = [
  {
    level: "Core",
    keywords: [
      "Java",
      "Dropwizard",
      "Spring Boot",
      "REST APIs",
      "JDBC",
      "SQL",
      "NoSQL",
      "Oracle Cloud Infrastructure (OCI)",
      "Distributed Systems",
      "Backend Development",
      "Retrieval-Augmented Generation (RAG)",
      "LLM Integration",
      "React",
      "TypeScript",
    ],
  },
  {
    level: "Working knowledge",
    keywords: [
      "Docker",
      "Node.js",
      "Express",
      "Next.js",
      "PostgreSQL",
      "MySQL",
      "MongoDB",
      "Microservices",
      "Event-Driven Architecture",
      "Kubernetes (OKE)",
      "Helm",
      "GraalVM",
      "Flutter",
      "Prompt Engineering",
      "Tool Calling",
      "MCP",
      "Agentic AI",
      "JWT / Auth",
      "System Design",
      "CI/CD",
    ],
  },
  {
    level: "Familiar",
    keywords: [
      "Terraform",
      "Infrastructure as Code",
      "Firebase",
      "Redux",
      "Dart",
      "C/C++",
      "Razorpay",
      "Agora SDK",
    ],
  },
];
